import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useSidebarOpenState } from '@backstage/core-components';
import { manewcoTokens } from '@platform/plugin-platform-react';

const useStyles = makeStyles(theme => {
  const MANEWCO = manewcoTokens(theme);
  return {
    link: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minHeight: 40,
      padding: '0 8px 0 12px',
      color: MANEWCO.navText,
      textDecoration: 'none',
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.79rem',
      fontWeight: 500,
      letterSpacing: -0.1,
      borderLeft: '3px solid transparent',
      '&:hover': { backgroundColor: MANEWCO.hover, color: MANEWCO.text },
    },
    active: { backgroundColor: MANEWCO.pale, color: MANEWCO.primary, borderLeftColor: MANEWCO.secondary },
    /** Menu réduit : icône seule, centrée dans la colonne, libellé en infobulle. */
    rail: { justifyContent: 'center', padding: 0, alignSelf: 'stretch' },
    label: { minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  };
});

interface Props {
  to: string;
  text: string;
  icon: ComponentType<any>;
  /** Actif seulement sur cette adresse exacte (ex. /catalog, pas les fiches). */
  exact?: boolean;
}

/**
 * Entrée d'un groupe du menu. Ligne propre plutôt que `SidebarItem` : la largeur fixe de celui-ci
 * ne laisse pas la place des libellés une fois indenté, et ses classes ne sont pas ciblables en
 * production (noms JSS raccourcis).
 */
export const SidebarGroupLink = ({ to, text, icon: Icon, exact }: Props) => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const base = isOpen ? classes.link : `${classes.link} ${classes.rail}`;
  return (
    <NavLink
      to={`/${to.replace(/^\/+/, '')}`}
      end={exact}
      aria-label={isOpen ? undefined : text}
      title={isOpen ? undefined : text}
      className={({ isActive }) => (isActive ? `${base} ${classes.active}` : base)}
    >
      <Icon fontSize="small" />
      {isOpen && <span className={classes.label}>{text}</span>}
    </NavLink>
  );
};
